import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/config";
import ProtocolCalendar from "@/components/ProtocolCalendar";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, User as UserIcon, Flame, ArrowLeft, ShieldAlert } from "lucide-react";

export default function AccountabilityAdminTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/users");
        setUsers(data);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Failed to load users");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openUser = async (userId) => {
    setSelectedId(userId);
    setHistoryLoading(true);
    setHistory(null);
    try {
      const { data } = await api.get(`/admin/users/${userId}/protocol-history?days=90`);
      setHistory(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const filtered = users.filter((u) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (u.name || "").toLowerCase().includes(s)
      || (u.email || "").toLowerCase().includes(s);
  });

  if (selectedId && history) {
    const u = history.user;
    const s = history.stats;
    const cadenceBadge = badgeFor(s.days_since_last_checkin);
    return (
      <div className="space-y-5" data-testid="accountability-detail">
        <button
          onClick={() => { setSelectedId(null); setHistory(null); }}
          data-testid="accountability-back"
          className="text-sm text-white/60 hover:text-white flex items-center gap-2"
        >
          <ArrowLeft size={14} /> Back to all members
        </button>

        <div className="glass rounded-2xl p-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FF8A00] to-[#E52E71] flex items-center justify-center text-lg font-semibold">
              {(u.name || u.email || "?")[0].toUpperCase()}
            </div>
            <div>
              <div className="font-display text-2xl">{u.name || "—"}</div>
              <div className="text-sm text-white/50">{u.email}</div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#FF8A00] mt-1">
                {u.tier} tier
              </div>
            </div>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium border ${cadenceBadge.classes}`}>
            {cadenceBadge.icon}
            {cadenceBadge.label}
          </div>
        </div>

        {historyLoading ? (
          <div className="text-white/50">Loading history…</div>
        ) : (
          <ProtocolCalendar entries={history.entries} stats={history.stats} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="accountability-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-white/85">
            <Flame size={18} className="text-[#FF8A00]" />
            <span className="font-display text-xl">Member accountability</span>
          </div>
          <p className="text-sm text-white/50 mt-1">
            Click a member to open their protocol calendar and see the last 90 days at a glance.
          </p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            data-testid="accountability-search"
            placeholder="Search name or email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-9 w-72 bg-black/40 border-white/10"
          />
        </div>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-white/60 text-[11px] tracking-[0.15em] uppercase">
              <tr>
                <th className="text-left px-4 py-3">Member</th>
                <th className="text-left px-4 py-3">Tier</th>
                <th className="text-left px-4 py-3">Streak</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-white/50">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-white/50">
                  <UserIcon className="mx-auto mb-3 text-white/30" size={24} />
                  No matches.
                </td></tr>
              ) : (
                filtered.map((u) => {
                  const badge = badgeFor(daysSince(u.last_checkin_day || null));
                  return (
                    <tr key={u.user_id}
                        onClick={() => openUser(u.user_id)}
                        data-testid={`accountability-row-${u.user_id}`}
                        className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer transition">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF8A00] to-[#E52E71] flex items-center justify-center text-xs font-semibold">
                            {(u.name || u.email || "?")[0].toUpperCase()}
                          </div>
                          <div>
                            <div>{u.name || "—"}</div>
                            <div className="text-xs text-white/45">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[10px] tracking-[0.22em] uppercase text-[#FF8A00]">{u.tier}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 text-white/85">
                          <Flame size={12} className="text-[#FF8A00]" /> {u.streak ?? 0}d
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${badge.classes}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-white/45">Open →</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function daysSince(iso) {
  if (!iso) return null;
  const then = new Date(iso);
  const now = new Date();
  return Math.floor((now - then) / 86400000);
}

function badgeFor(days) {
  if (days == null) return {
    label: "No check-ins",
    classes: "bg-white/5 text-white/40 border-white/10",
    icon: null,
  };
  if (days <= 1) return {
    label: "Active",
    classes: "bg-[#FF8A00]/10 text-[#FF8A00] border-[#FF8A00]/30",
    icon: <Flame size={12} />,
  };
  if (days <= 3) return {
    label: `${days}d idle`,
    classes: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
    icon: null,
  };
  return {
    label: `${days}d away`,
    classes: "bg-red-500/10 text-red-400 border-red-500/30",
    icon: <ShieldAlert size={12} />,
  };
}
